/**
 * PAYMENT TILLS. A handle a participant hands out to be paid in crypto.
 *
 * The Kenyan reference is exact and deliberate: this is Lipa na M-PESA's till, pointed at a crypto
 * receiving wallet. A shopkeeper writes `LPUSDTTRC204K7Q2M` on a card by the counter, a customer
 * types it into Send Money, and neither of them ever sees a thirty-four character address. That
 * substitution is the whole product: a till is short enough to say out loud, and an address that is
 * mistyped by one character is money that is gone.
 *
 * ## Why the asset and the chain are IN the handle
 *
 * The two facts that decide whether a crypto payment arrives are which asset and which network. USDT
 * exists on Tron, Ethereum and BNB Smart Chain, the addresses look different, and sending on the
 * wrong one is unrecoverable. A bare code would have to be explained out of band every single time it
 * was used, and the explanation is exactly what gets lost when a number is copied out of a WhatsApp
 * message. So the handle states both, this screen shows them again in words, and Send Money checks
 * that the handle and the record agree before anybody pays.
 *
 * ## Three things this screen must not do
 *
 * 1. **Never show key material.** A till carries a number, an asset, a network and a receiving
 *    address. There is no seed, no private key and no custody handle on the object at all, which is a
 *    property of the API shape (`Till` in lib/api.ts) rather than a rule this component follows.
 * 2. **Never invent a chain.** The asset and network lists are SERVED (`/tills/options`). Nothing in
 *    the backend knows what a network is yet, so a hardcoded list here would be a screen asserting an
 *    operational fact nobody has established.
 * 3. **Never claim a chain is live.** Custody is an MPC simulator (CLAUDE.md gap 10), so no address
 *    here settles anything. The warning is rendered from data served with the record and the
 *    catalogue, so it cannot be forgotten here and does not linger once custody is real.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';

import { LipaApiError, type Till as TillRecord, type TillNetwork } from '@/lib/api';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { TierGate } from '@/components/gate';
import { BRAND_PINE, WHITE } from '@/lib/brand-colors';
import { copyText } from '@/lib/clipboard';
import { fmtDate } from '@/lib/format';
import { buildTillPoster, canvasToBitmap, tillFileStem, tillPaymentUri } from '@/lib/till-poster';
import { tillSegments, tillService, tillShareText } from '@/lib/till';
import { useResource } from '@/lib/use-resource';

export default function TillPage() {
  return (
    // Receiving needs an identity, exactly as Receive does: a till is a claim that money sent to it
    // reaches a verified person, and at T0 there is no wallet behind it to reach.
    <TierGate requires="T1" what="A payment Till">
      <TillScreen />
    </TierGate>
  );
}

function TillScreen() {
  const options = useResource(() => tillService.options(), []);
  const tills = useResource(() => tillService.listUserTills(), []);

  const [asset, setAsset] = useState('');
  const [networkTag, setNetworkTag] = useState('');
  const [generating, setGenerating] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<string | null>(null);

  const catalogue = useMemo(() => options.data?.assets ?? [], [options.data]);
  const active = useMemo(
    () => (tills.data ?? []).filter((t) => t.status === 'active'),
    [tills.data],
  );
  const retired = useMemo(
    () => (tills.data ?? []).filter((t) => t.status !== 'active'),
    [tills.data],
  );

  // Default to the first asset the service offers. Never to a literal: the catalogue decides what
  // exists, and a hardcoded default is the first thing to go stale when it changes.
  useEffect(() => {
    if (!asset && catalogue.length) setAsset(catalogue[0].asset);
  }, [catalogue, asset]);

  const networks: TillNetwork[] = useMemo(
    () => catalogue.find((a) => a.asset === asset)?.networks ?? [],
    [catalogue, asset],
  );

  /**
   * CHANGING THE ASSET RESETS THE CHAIN. Always, even when the old tag exists on the new asset.
   *
   * The first version of this kept a still-valid selection, and that is a trap: ERC20 is offered for
   * both USDC and USDT, so choosing USDC on Ethereum and then switching to USDT left ERC20 selected
   * and the till was issued on Ethereum, silently, while the participant's attention was on the asset
   * they had just changed. The tag was the same string; the destination was a different chain and a
   * different token contract.
   *
   * A carried-over chain is exactly the class of silent mismatch this whole feature exists to
   * prevent, so the network is always chosen against the asset currently in view. The list is ordered
   * by what a Kenyan participant most likely wants (TRC20 first for USDT), and that default is
   * re-applied every time the asset changes.
   */
  useEffect(() => {
    setNetworkTag(networks[0]?.tag ?? '');
  }, [asset, networks]);

  const chosenNetwork = networks.find((n) => n.tag === networkTag) ?? null;
  const existing = active.find((t) => t.asset === asset && t.network_tag === networkTag) ?? null;

  const generate = useCallback(async () => {
    if (!asset || !networkTag) return;
    setGenerating(true);
    setFailure(null);
    try {
      const till = await tillService.generateTill(asset, networkTag);
      setJustCreated(till.till_number);
      tills.reload();
    } catch (err) {
      setFailure(
        err instanceof LipaApiError
          ? err.detail.message
          : 'We could not create your Till just now. Nothing has changed on your account.',
      );
    } finally {
      setGenerating(false);
    }
  }, [asset, networkTag, tills]);

  if (options.loading || tills.loading) return <LoadingCard lines={5} />;
  if (options.error || tills.error)
    return (
      <ErrorState
        body="We could not load your Tills."
        onRetry={() => {
          options.reload();
          tills.reload();
        }}
      />
    );

  return (
    <div className="mx-auto max-w-md space-y-5">
      <Link
        to="/home"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Home
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Payment Tills</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          A Till is a short number others use to pay you in crypto. It names the asset and the
          network it accepts, so nobody has to ask.
        </p>
      </header>

      {failure ? (
        <p
          role="alert"
          className="rounded-card border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
        >
          {failure}
        </p>
      ) : null}

      {/* WHAT YOU ALREADY HAVE, first. Somebody arriving here usually wants to find their Till, not
          make another one. */}
      {active.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-ink">Your Tills</h2>
          {active.map((t) => (
            <TillCard
              key={t.id}
              till={t}
              highlight={t.till_number === justCreated}
              onChanged={() => tills.reload()}
            />
          ))}
        </section>
      ) : (
        <EmptyState
          title="You have no Till yet"
          body="Choose an asset and a network below, and we will issue one. It takes a moment and nothing is locked."
        />
      )}

      {/* GENERATE. Asset first, then the chains that asset actually runs on. */}
      <section className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card">
        <div>
          <h2 className="font-semibold text-ink">Generate a Till</h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            One Till per asset and network. Pick what you want to be paid in.
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-ink">Asset</span>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {catalogue.map((a) => (
              <button
                key={a.asset}
                type="button"
                aria-pressed={asset === a.asset}
                onClick={() => setAsset(a.asset)}
                className={`rounded-field border px-3 py-2.5 text-left transition ${
                  asset === a.asset
                    ? 'border-primary bg-primary-tint'
                    : 'border-border hover:bg-surface-sunken'
                }`}
              >
                <span
                  className={`block text-sm font-semibold ${asset === a.asset ? 'text-brand' : 'text-ink'}`}
                >
                  {a.asset}
                </span>
                <span className="block truncate text-micro text-ink-muted">{a.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-ink">Network</span>
          {/*
            DYNAMIC, and it is the consequential field. The list is whatever the chosen asset runs
            on, and the tag shown is the one exchanges use, because that is the word the payer will
            be looking for on their own screen.
          */}
          <div className="mt-2 space-y-2">
            {networks.map((n) => (
              <label
                key={n.tag}
                className={`flex cursor-pointer items-start justify-between gap-3 rounded-field border px-3 py-3 text-sm transition ${
                  networkTag === n.tag
                    ? 'border-primary bg-primary-tint'
                    : 'border-border hover:bg-surface-sunken'
                }`}
              >
                <span className="min-w-0">
                  <span
                    className={`block font-medium ${networkTag === n.tag ? 'text-brand' : 'text-ink'}`}
                  >
                    {n.label} <span className="font-mono text-xs text-ink-muted">({n.tag})</span>
                  </span>
                  {n.note ? (
                    <span className="mt-0.5 block text-xs text-ink-muted">{n.note}</span>
                  ) : null}
                  {!n.live ? (
                    <span className="mt-0.5 block text-xs text-warning">
                      Not live yet: addresses on this network are simulated.
                    </span>
                  ) : null}
                </span>
                <input
                  type="radio"
                  name="till-network"
                  value={n.tag}
                  checked={networkTag === n.tag}
                  onChange={() => setNetworkTag(n.tag)}
                  className="mt-0.5 shrink-0 accent-primary"
                />
              </label>
            ))}
          </div>
        </div>

        {/* The mismatch that loses money, said before the Till exists rather than after. */}
        <p className="rounded-card bg-surface-sunken p-3 text-xs text-ink-muted">
          A Till accepts {asset || 'this asset'}
          {chosenNetwork ? ` on ${chosenNetwork.label} (${chosenNetwork.tag})` : ''} and nothing
          else. Anything sent on another network will not arrive, and cannot be recovered. That is
          why the network is part of the Till number.
        </p>

        {existing ? (
          <p className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink">
            You already have a Till for this pair:{' '}
            <span className="font-mono font-medium">{existing.till_number}</span>. One Till per
            asset and network keeps the mapping unambiguous, so there is nothing to add.
          </p>
        ) : (
          <button
            onClick={generate}
            disabled={generating || !asset || !networkTag}
            className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          >
            {generating
              ? 'Generating...'
              : `Generate ${asset || ''} Till${chosenNetwork ? ` (${chosenNetwork.tag})` : ''}`}
          </button>
        )}
      </section>

      {retired.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-ink">Retired Tills</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface">
            {retired.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <span className="truncate font-mono text-ink-muted line-through">
                  {t.till_number}
                </span>
                <span className="shrink-0 text-xs text-ink-subtle">Retired</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ink-subtle">
            A retired Till stops resolving, so payments to it are refused rather than misdelivered.
            Everything already paid to it is unaffected.
          </p>
        </section>
      ) : null}
    </div>
  );
}

function TillCard({
  till,
  highlight,
  onChanged,
}: {
  till: TillRecord;
  highlight?: boolean;
  onChanged: () => void;
}) {
  const [copied, setCopied] = useState<'till' | 'address' | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const [details, setDetails] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [posterNote, setPosterNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<'download' | 'share' | null>(null);

  const parts = tillSegments(till);

  /**
   * THE QR IS RENDERED AT 512px AND DISPLAYED AT 176.
   *
   * One canvas serves both jobs. On screen the extra resolution is what makes it sharp on a phone;
   * in the poster it is the source bitmap, read back pixel by pixel and drawn into the PDF as a
   * lossless 1-bit image. Rendering a second, larger QR just for printing would be two code paths
   * that could disagree about what they encode, and the one thing a payment QR must never do is
   * encode something other than what the screen beside it says.
   */
  const qrRef = useRef<HTMLCanvasElement>(null);

  const poster = useCallback((): { blob: Blob; filename: string } | null => {
    const canvas = qrRef.current;
    const bitmap = canvas ? canvasToBitmap(canvas) : null;
    if (!bitmap) return null;
    return { blob: buildTillPoster(till, bitmap), filename: `${tillFileStem(till)}.pdf` };
  }, [till]);

  const downloadPoster = useCallback(() => {
    setBusy('download');
    setPosterNote(null);
    try {
      const built = poster();
      if (!built) {
        setPosterNote('We could not build the poster. Reload the page and try again.');
        return;
      }
      // An object URL and a synthetic click: the file never leaves the device, which is the point.
      // A poster built on a server would mean posting a participant's payment handle to one.
      const url = URL.createObjectURL(built.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = built.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setPosterNote('Poster downloaded. Print it at any size: the QR is vector-sharp.');
    } finally {
      setBusy(null);
    }
  }, [poster]);

  /**
   * Share the poster itself, and fall back in the order that keeps the QR shareable.
   *
   * A phone shares the PDF. A browser that can share files but not this one gets the QR as a PNG,
   * which is what a person pastes into WhatsApp anyway. A browser with no file sharing at all gets
   * the text, and the poster is offered as a download instead. Every step is stated, because a share
   * button that silently did something else is worse than one that says what it did.
   */
  const sharePoster = useCallback(async () => {
    setBusy('share');
    setPosterNote(null);

    /**
     * A WATCHDOG, because `navigator.share` is not guaranteed to settle.
     *
     * The promise resolves when the share sheet completes and rejects when it is dismissed, and on
     * some platforms (a headless browser, an embedded webview with no sheet to show) it does
     * neither. Awaiting it forever leaves a button reading "Sharing..." and permanently disabled,
     * which is the failure mode this app's own rule about submit buttons exists to prevent. The
     * watchdog only re-enables the button; it never invents an outcome or fires a fallback, because
     * a sheet that is genuinely open on somebody's phone must not be second-guessed from here.
     */
    const release = () => setBusy(null);
    const watchdog = setTimeout(release, 10_000);

    /**
     * The share sheet is a surface OVER this page, so coming back to the page is the actual signal
     * that it closed. Listening for that releases the button the moment the person returns instead
     * of making them wait out the watchdog, which measured eight seconds of a dead button in
     * testing. The watchdog stays as the backstop for platforms where visibility never changes.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible') release();
    };
    document.addEventListener('visibilitychange', onVisible);

    /**
     * DISMISSED IS NOT FAILED, and the difference decides what happens next.
     *
     * `navigator.share` rejects with AbortError when the person closes the share sheet: that is them
     * changing their mind and nothing should happen. Every other rejection is the browser saying it
     * could not do it, and swallowing that leaves somebody pressing a button that does nothing at
     * all. So a dismissal stops here and a failure falls through to the next option.
     */
    const attempt = async (data: ShareData): Promise<'shared' | 'dismissed' | 'failed'> => {
      try {
        await navigator.share(data);
        return 'shared';
      } catch (err) {
        return (err as Error)?.name === 'AbortError' ? 'dismissed' : 'failed';
      }
    };

    try {
      const built = poster();
      const canvas = qrRef.current;

      // 1. The poster itself, as a PDF. What a shopkeeper actually wants to send to a printer.
      if (built && navigator.canShare) {
        const pdf = new File([built.blob], built.filename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [pdf] })) {
          const outcome = await attempt({
            title: `VASP ${till.asset} Till`,
            files: [pdf],
          });
          if (outcome !== 'failed') return;
        }
      }

      // 2. The barcode as an image, which is what goes into a chat.
      const png = canvas
        ? await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
        : null;
      if (png && navigator.canShare) {
        const image = new File([png], `${tillFileStem(till)}.png`, { type: 'image/png' });
        if (navigator.canShare({ files: [image] })) {
          const outcome = await attempt({
            title: `VASP ${till.asset} Till`,
            text: tillShareText(till),
            files: [image],
          });
          if (outcome !== 'failed') return;
        }
      }

      // 3. The details as text, which every share sheet can carry.
      if ('share' in navigator) {
        const outcome = await attempt({
          title: `VASP ${till.asset} Till`,
          text: tillShareText(till),
        });
        if (outcome === 'dismissed') return;
        if (outcome === 'shared') {
          setPosterNote('Your browser cannot share files, so we shared the Till details instead.');
          return;
        }
      }

      // 4. Nothing shareable. Hand over the file rather than leaving a dead button.
      downloadPoster();
      setPosterNote('Sharing is not available in this browser, so the poster was downloaded.');
    } finally {
      clearTimeout(watchdog);
      document.removeEventListener('visibilitychange', onVisible);
      setBusy(null);
    }
  }, [poster, till, downloadPoster]);

  const copy = useCallback(async (what: 'till' | 'address', value: string) => {
    setCopyFailed(false);
    const ok = await copyText(value);
    if (ok) {
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } else {
      // The clipboard is unavailable on an insecure origin, which is exactly how somebody tests this
      // on a phone over the LAN. Say so, rather than leaving a button that looks like it worked.
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 3000);
    }
  }, []);

  const share = useCallback(async () => {
    const text = tillShareText(till);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'VASP Till', text });
        return;
      } catch {
        /* the share sheet was dismissed; fall through to the clipboard */
      }
    }
    void copy('till', text);
  }, [till, copy]);

  const retire = useCallback(async () => {
    setRetiring(true);
    try {
      await tillService.deactivateTill(till.till_number);
      onChanged();
    } finally {
      setRetiring(false);
    }
  }, [till.till_number, onChanged]);

  return (
    <section
      className={`space-y-4 rounded-xl border bg-surface p-5 shadow-card ${
        highlight ? 'border-primary' : 'border-border'
      }`}
    >
      <div className="text-center">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
          {till.asset} Till &middot; {till.network}
        </p>

        {/*
          THE HANDLE, WITH ITS PARTS LEGIBLE.

          One string, copied whole, but the asset and network segments are tinted so a person reading
          it off a screen sees what it accepts without parsing anything. `break-all` because it must
          never push the card sideways on a phone.
        */}
        {/*
          ONE UNBROKEN VALUE, separated by TYPOGRAPHY rather than by characters.

          The handle has no hyphens in it: it is typed on a phone keypad at a counter, and it copies,
          scans and dictates as a single token. The parts are still legible because they are tinted
          and given a hair of space, which costs the value nothing. `select-all` means a tap or a
          double click takes the whole thing, separators being absent rather than merely invisible.
        */}
        <p className="mt-1 select-all break-all font-mono text-2xl font-semibold tracking-tight text-ink">
          {parts ? (
            <>
              <span className="text-ink-subtle">LP</span>
              <span className="ml-1 text-brand">{parts.asset}</span>
              <span className="ml-1 text-brand">{parts.networkTag}</span>
              <span className="ml-1.5">{parts.code}</span>
            </>
          ) : (
            till.till_number
          )}
        </p>

        <p className="mt-2 inline-flex items-center gap-1.5 rounded-pill bg-success/10 px-2.5 py-0.5 text-micro font-medium text-success">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          Active
        </p>

        {/* The QR sits on a white tile in BOTH themes, on purpose. A QR needs high contrast to scan,
            and a "dark mode" QR with light modules on a near-black card is one phones struggle with.
            Same rule as the Receive screen, and the same reason. */}
        <div className="mx-auto mt-4 w-fit rounded-xl bg-white p-3 shadow-soft">
          <QRCodeCanvas
            ref={qrRef}
            value={tillPaymentUri(till)}
            size={512}
            style={{ width: '168px', height: '168px' }}
            fgColor={BRAND_PINE}
            bgColor={WHITE}
            level="M"
            marginSize={2}
          />
        </div>
        <p className="mt-2 text-xs text-ink-subtle">
          Scan to pay this Till, or read the number above.
        </p>
      </div>

      <dl className="space-y-2 border-t border-border pt-4 text-sm">
        <Row label="Asset" value={till.asset} />
        <Row label="Network" value={`${till.network} (${till.network_tag})`} />
        <Row label="Created" value={fmtDate(till.created_at)} />
      </dl>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => copy('till', till.till_number)}
          className="rounded-field border border-border bg-surface py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          {copied === 'till' ? 'Till copied' : 'Copy Till'}
        </button>
        <button
          onClick={share}
          className="flex items-center justify-center gap-2 rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M8.7 10.7 15.3 7m0 10-6.6-3.7M18 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
            />
          </svg>
          Share
        </button>
      </div>

      {/* THE PRINTABLE ARTEFACT. A poster is how a till reaches a counter, and a counter is where a
          till is actually used. Both buttons work entirely on the device. */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={downloadPoster}
          disabled={busy !== null}
          className="flex items-center justify-center gap-2 rounded-field border border-border bg-surface py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-50"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M12 4v11m0 0 4-4m-4 4-4-4M5 17v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2"
            />
          </svg>
          {busy === 'download' ? 'Preparing...' : 'Download poster'}
        </button>
        <button
          onClick={sharePoster}
          disabled={busy !== null}
          className="flex items-center justify-center gap-2 rounded-field border border-border bg-surface py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-50"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M12 15V4m0 0 4 4m-4-4L8 8M5 15v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4"
            />
          </svg>
          {busy === 'share' ? 'Sharing...' : 'Share poster'}
        </button>
      </div>

      {posterNote ? (
        <p
          role="status"
          className="rounded-card bg-surface-sunken p-3 text-center text-xs text-ink-muted"
        >
          {posterNote}
        </p>
      ) : null}

      {copyFailed ? (
        <p role="alert" className="rounded-card bg-danger/5 p-3 text-center text-xs text-danger">
          We could not copy automatically. Press and hold the Till number above to copy it by hand.
        </p>
      ) : null}

      <button
        onClick={() => setDetails((d) => !d)}
        aria-expanded={details}
        className="w-full text-center text-sm font-medium text-brand hover:underline"
      >
        {details ? 'Hide details' : 'View details'}
      </button>

      {details ? (
        <div className="space-y-3 rounded-card bg-surface-sunken p-3">
          <div>
            <p className="text-xs font-medium text-ink">Mapped wallet</p>
            <p className="mt-1 break-all font-mono text-xs text-ink-muted">{till.wallet_address}</p>
            <button
              onClick={() => copy('address', till.wallet_address)}
              className="mt-1.5 text-xs font-medium text-brand hover:underline"
            >
              {copied === 'address' ? 'Address copied' : 'Copy address'}
            </button>
          </div>

          {/*
            THE HONEST PART. Custody is a simulator, so this address is not one any chain will settle
            to. Served as a field rather than written here, so it stops being said the day it stops
            being true.
          */}
          {till.address_is_simulated ? (
            <p className="rounded-field border border-warning/30 bg-warning/5 p-2.5 text-xs text-ink">
              <span className="font-medium">This address is simulated.</span> The platform&rsquo;s
              custody is a simulator in the sandbox, so nothing sent to it on the real{' '}
              {till.network} network will arrive. Use the Till inside VASP, and do not post
              this address anywhere.
            </p>
          ) : null}

          <button
            onClick={retire}
            disabled={retiring}
            className="text-xs font-medium text-danger hover:underline disabled:opacity-50"
          >
            {retiring ? 'Retiring...' : 'Retire this Till'}
          </button>
        </div>
      ) : null}

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs text-ink-muted">
        Accepts {till.asset} on {till.network} only. Your Till never reveals your wallet keys.{' '}
        <Link to="/transact" className="font-medium text-brand underline">
          Pay a Till
        </Link>
      </p>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-ink">{value}</dd>
    </div>
  );
}
